import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { Badge, estadoVisible, mapaEstadosSolicitud } from "../utils/estados";
import Cargando from "../components/Cargando";
import { Ayuda } from "../context/AyudaContext";

// "En cola" y "Cotizando" son el mismo estado en la base (en_cola) — se
// distinguen por si ya tiene o no al menos una cotización registrada.
// `queryEstado` es lo que se manda como ?estado= al hacer clic en el tile —
// para los 3 primeros son filtros "virtuales" (ver construirFiltros en el
// backend) porque en la BD comparten el mismo valor real. `destino` manda a
// otra pantalla en vez de filtrar la tabla de aquí abajo (Entregadas y
// Rechazadas viven en Histórico, no en el Dashboard).
// Etapas del proceso: agrupan los tiles para leer de un vistazo en qué va cada compra.
const ETAPAS = [
  { titulo: "Recepción", tiles: ["Nuevas", "En cola", "Cotizando"] },
  { titulo: "Aprobación", tiles: ["Esperando aprobación", "Aprobadas"] },
  { titulo: "Pago y compra", tiles: ["En proceso de pago", "Pagadas", "En proceso de compra"] },
  { titulo: "Cierre", tiles: ["Lista para retiro", "Entregadas", "Rechazadas"] },
];

const TILES = [
  {
    texto: "Nuevas",
    icono: "📄",
    color: "#5b6472",
    bgIcono: "#eef1f3",
    filtro: (s) => s.estado === "nueva",
    queryEstado: "nueva",
  },
  {
    texto: "En cola",
    icono: "🕐",
    color: "var(--tema-primario, #2f6690)",
    bgIcono: "var(--tema-suave, #dceaf5)",
    filtro: (s) => s.estado === "en_cola" && !s.total_cotizaciones && !s.en_atencion && !s.tiene_pendiente_aprobacion,
    queryEstado: "en_cola_solo",
  },
  {
    texto: "Cotizando",
    icono: "💲",
    color: "var(--tema-titulo, #0b3c60)",
    bgIcono: "var(--tema-suave, #dceaf5)",
    filtro: (s) => s.estado === "en_cola" && (s.total_cotizaciones > 0 || s.en_atencion) && !s.tiene_pendiente_aprobacion,
    queryEstado: "cotizando_solo",
  },
  {
    texto: "Esperando aprobación",
    icono: "⏳",
    color: "#0ea5e9",
    bgIcono: "#e0f2fe",
    filtro: (s) => s.estado === "en_cola" && s.tiene_pendiente_aprobacion,
    queryEstado: "pendiente_aprobacion",
  },
  {
    texto: "Aprobadas",
    icono: "✅",
    color: "#2f7a54",
    bgIcono: "#dcf0e6",
    filtro: (s) => s.estado === "aprobada" && !s.tiene_pago_pendiente && !s.tiene_pago_realizado,
    queryEstado: "aprobada",
  },
  {
    texto: "En proceso de pago",
    icono: "💳",
    color: "#0f7c8a",
    bgIcono: "#d5eef1",
    filtro: (s) => s.estado === "aprobada" && !!s.tiene_pago_pendiente,
    queryEstado: "en_proceso_pago",
  },
  {
    texto: "Pagadas",
    icono: "💰",
    color: "#1e5e3f",
    bgIcono: "#c9ead8",
    filtro: (s) => s.estado === "aprobada" && !s.tiene_pago_pendiente && !!s.tiene_pago_realizado,
    queryEstado: "pagada",
  },
  {
    texto: "En proceso de compra",
    icono: "🛒",
    color: "#0ea5e9",
    bgIcono: "#e0f2fe",
    filtro: (s) => s.estado === "en_proceso_compra",
    queryEstado: "en_proceso_compra",
  },
  {
    texto: "Lista para retiro",
    icono: "📦",
    color: "#f97316",
    bgIcono: "#fde8d7",
    filtro: (s) => s.estado === "lista_para_retiro",
    queryEstado: "lista_para_retiro",
  },
  {
    texto: "Entregadas",
    icono: "🚚",
    color: "#2f7a54",
    bgIcono: "#dcf0e6",
    filtro: (s) => s.estado === "entregada",
    destino: "/historico?estado=entregada",
  },
  {
    texto: "Rechazadas",
    icono: "❌",
    color: "#a3402f",
    bgIcono: "#f7ded9",
    filtro: (s) => s.estado === "rechazada",
    destino: "/historico?estado=rechazada",
  },
];

// Entregada y Rechazada no aparecen aquí — el Dashboard solo muestra lo
// activo/pendiente; esas dos se consultan desde Histórico.
const ESTADOS_FILTRO = [
  { valor: "", texto: "Todos los estados" },
  { valor: "nueva", texto: "Nueva" },
  { valor: "en_cola_solo", texto: "En cola" },
  { valor: "cotizando_solo", texto: "Cotizando" },
  { valor: "pendiente_aprobacion", texto: "Esperando aprobación" },
  { valor: "aprobada", texto: "Aprobada" },
  { valor: "en_proceso_pago", texto: "Proceso de pago" },
  { valor: "pagada", texto: "Pagada" },
  { valor: "en_proceso_compra", texto: "Proceso de compra" },
  { valor: "lista_para_retiro", texto: "Lista para retiro" },
];

const TAMANOS_PAGINA = ["5", "20", "50", "100", "todas"];

function Dashboard() {
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const esStaff =
    usuario?.rol === "compras" || usuario?.rol === "aprobador" || usuario?.rol === "administrador";
  const navigate = useNavigate();
  const tablaRef = useRef(null);

  const [areas, setAreas] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [listado, setListado] = useState([]);
  const [totalListado, setTotalListado] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [cargandoListado, setCargandoListado] = useState(false);
  const [error, setError] = useState("");

  const [filtroArea, setFiltroArea] = useState("");
  const [filtroSolicitante, setFiltroSolicitante] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");
  const [tamanoPagina, setTamanoPagina] = useState("5");
  const [pagina, setPagina] = useState(1);
  const [ordenPor, setOrdenPor] = useState("fecha");
  const [ordenAsc, setOrdenAsc] = useState(false);

  useEffect(() => {
    apiFetch("/api/areas")
      .then((data) => setAreas(data || []))
      .catch(() => {});
  }, []);

  // Resumen sin filtrar/paginar — de aquí salen los conteos de las tarjetas.
  useEffect(() => {
    const endpoint = esStaff ? "/api/solicitudes" : "/api/solicitudes/mias";
    apiFetch(endpoint)
      .then((data) => setResumen(data?.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  }, [esStaff]);

  // Listado filtrado + paginado — es lo que se ve en la tabla de abajo.
  // Solo lo activo/pendiente — lo entregado y lo rechazado vive en Histórico.
  useEffect(() => {
    setCargandoListado(true);
    const params = new URLSearchParams({ activas: "1" });
    if (filtroArea) params.set("area_id", filtroArea);
    if (esStaff && filtroSolicitante) params.set("solicitante", filtroSolicitante);
    if (filtroEstado) params.set("estado", filtroEstado);
    if (filtroDesde) params.set("desde", filtroDesde);
    if (filtroHasta) params.set("hasta", filtroHasta);
    if (tamanoPagina !== "todas") {
      params.set("limit", tamanoPagina);
      params.set("page", pagina);
    }
    const endpoint = esStaff ? "/api/solicitudes" : "/api/solicitudes/mias";
    apiFetch(`${endpoint}?${params.toString()}`)
      .then((data) => {
        setListado(data?.items || []);
        setTotalListado(data?.total || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargandoListado(false));
  }, [esStaff, filtroArea, filtroSolicitante, filtroEstado, filtroDesde, filtroHasta, tamanoPagina, pagina]);

  const contar = (filtro) => resumen.filter(filtro).length;

  const cambiarFiltro = (setter) => (valor) => {
    setter(valor);
    setPagina(1);
  };

  // Los tiles son "atajos": tocar uno filtra la tabla de abajo por ese
  // estado (o, para Entregadas/Rechazadas, manda al Histórico, que es donde
  // vive lo archivado) y lleva la vista hasta la tabla.
  const tocarTile = (tile) => {
    if (tile.destino) {
      navigate(tile.destino);
      return;
    }
    setFiltroEstado(tile.queryEstado);
    setPagina(1);
    tablaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const limpiarFiltros = () => {
    setFiltroArea("");
    setFiltroSolicitante("");
    setFiltroEstado("");
    setFiltroDesde("");
    setFiltroHasta("");
    setPagina(1);
  };

  const hayFiltrosActivos =
    filtroArea || filtroSolicitante || filtroEstado || filtroDesde || filtroHasta;

  const totalPaginas =
    tamanoPagina === "todas" ? 1 : Math.max(1, Math.ceil(totalListado / Number(tamanoPagina)));

  // Orden solo dentro de lo ya cargado en la página actual — no hay orden
  // en el backend, así que si cambian de página el orden se recalcula sobre
  // esa nueva tanda.
  const alternarOrden = (campo) => {
    if (ordenPor === campo) {
      setOrdenAsc((v) => !v);
    } else {
      setOrdenPor(campo);
      setOrdenAsc(true);
    }
  };

  const listadoOrdenado = [...listado].sort((a, b) => {
    // Las urgentes siempre primero, sin importar por qué columna se esté
    // ordenando — no se pueden quedar perdidas más abajo en la tabla.
    if (!!a.tiene_urgente !== !!b.tiene_urgente) return a.tiene_urgente ? -1 : 1;
    const va = ordenPor === "solicitante" ? a.solicitante_nombre || "" : a.creado_en;
    const vb = ordenPor === "solicitante" ? b.solicitante_nombre || "" : b.creado_en;
    if (va < vb) return ordenAsc ? -1 : 1;
    if (va > vb) return ordenAsc ? 1 : -1;
    return 0;
  });

  if (cargando) return <Cargando />;

  return (
    <div>
      <h2 style={{ marginBottom: 0 }}>Hola, {usuario?.nombre?.split(" ")[0] || ""} 👋</h2>
      <Ayuda>
      <p style={{ color: "#5b6472", marginTop: "0.2rem" }}>
        {esStaff ? "Resumen de todas las solicitudes" : "Resumen de tus solicitudes"}
      </p>
      </Ayuda>

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}

      <div className="dashboard-etapas" style={styles.etapas}>
        {ETAPAS.map((etapa) => (
          <section key={etapa.titulo} style={{ ...styles.etapa, gridColumn: `span ${etapa.tiles.length}` }}>
            <div style={styles.etapaTitulo}>{etapa.titulo}</div>
            <div
              className="dashboard-tiles"
              style={{ ...styles.tiles, gridTemplateColumns: `repeat(${etapa.tiles.length}, minmax(0, 1fr))` }}
            >
              {etapa.tiles.map((nombre) => {
                const t = TILES.find((tile) => tile.texto === nombre);
                // El solicitante las recibió; el personal las entregó.
                const texto = t.texto === "Entregadas" && usuario?.rol === "solicitante" ? "Recibidas" : t.texto;
                return (
                  <button
                    key={t.texto}
                    type="button"
                    onClick={() => tocarTile(t)}
                    style={{
                      ...styles.tile,
                      ...(filtroEstado && filtroEstado === t.queryEstado ? styles.tileActivo : {}),
                    }}
                    title={`Ver solicitudes ${texto.toLowerCase()}`}
                  >
                    <div style={styles.tileTop}>
                      <div style={{ ...styles.iconoTile, background: t.bgIcono, color: t.color }}>{t.icono}</div>
                      <div style={styles.tileNumero}>{contar(t.filtro)}</div>
                    </div>
                    <div style={styles.tileTexto}>{texto}</div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div style={styles.card} ref={tablaRef}>
        <div style={styles.cardHeaderSolicitudes}>
          <span />
          <h3 style={{ margin: 0, textAlign: "center" }}>{esStaff ? "Solicitudes" : "Mis solicitudes"}</h3>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            {esStaff && (
              <Link to="/bandeja" style={styles.linkVerTodas}>
                Ver todas →
              </Link>
            )}
            <Link to="/nueva" style={styles.botonNueva}>
              + Nueva solicitud
            </Link>
          </div>
        </div>

        <div className="barra-filtros" style={styles.filtros}>
          {esStaff && (
            <select
              value={filtroArea}
              onChange={(e) => cambiarFiltro(setFiltroArea)(e.target.value)}
              style={styles.filtroInput}
            >
              <option value="">Todas las áreas</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </select>
          )}

          {esStaff && (
            <div style={{ position: "relative" }}>
              <span style={styles.iconoBusqueda}>🔍</span>
              <input
                type="text"
                placeholder="Buscar por solicitante..."
                value={filtroSolicitante}
                onChange={(e) => cambiarFiltro(setFiltroSolicitante)(e.target.value)}
                style={{ ...styles.filtroInput, paddingLeft: "1.9rem" }}
              />
            </div>
          )}

          <select
            value={filtroEstado}
            onChange={(e) => cambiarFiltro(setFiltroEstado)(e.target.value)}
            style={styles.filtroInput}
          >
            {ESTADOS_FILTRO.map((op) => (
              <option key={op.valor} value={op.valor}>
                {op.texto}
              </option>
            ))}
          </select>

          <label className="filtro-fecha" style={styles.etiquetaFecha}>
            Desde
            <input
              type="date"
              value={filtroDesde}
              onChange={(e) => cambiarFiltro(setFiltroDesde)(e.target.value)}
              style={styles.filtroInput}
            />
          </label>
          <label className="filtro-fecha" style={styles.etiquetaFecha}>
            Hasta
            <input
              type="date"
              value={filtroHasta}
              onChange={(e) => cambiarFiltro(setFiltroHasta)(e.target.value)}
              style={styles.filtroInput}
            />
          </label>

          <button
            type="button"
            onClick={limpiarFiltros}
            disabled={!hayFiltrosActivos}
            style={{ ...styles.botonLimpiar, opacity: hayFiltrosActivos ? 1 : 0.5 }}
          >
            🧹 Limpiar
          </button>
        </div>

        {cargandoListado ? (
          <Cargando compacto />
        ) : listado.length === 0 ? (
          <p style={{ marginTop: "0.75rem", textAlign: "center" }}>
            {hayFiltrosActivos ? (
              "Ninguna solicitud coincide con los filtros."
            ) : (
              // Aquí solo se listan las solicitudes en curso: las entregadas y
              // rechazadas están en el Histórico (por eso no se dice "no hay
              // solicitudes" cuando sí hay, pero archivadas).
              <>
                No hay solicitudes en curso. <Link to="/nueva">Crea una nueva</Link> o revisa las
                entregadas y rechazadas en el <Link to="/historico">Histórico</Link>.
              </>
            )}
          </p>
        ) : (
          <div className="table-scroll">
          <table className="tabla-tarjetas" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", marginTop: "0.75rem" }}>
            <thead>
              <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                <th style={styles.th}>Solicitud</th>
                {esStaff && (
                  <th style={styles.thOrdenable} onClick={() => alternarOrden("solicitante")}>
                    Solicitante {ordenPor === "solicitante" ? (ordenAsc ? "↑" : "↓") : "↕"}
                  </th>
                )}
                <th style={styles.thOrdenable} onClick={() => alternarOrden("fecha")}>
                  Fecha {ordenPor === "fecha" ? (ordenAsc ? "↑" : "↓") : "↕"}
                </th>
                <th style={styles.th}>Área</th>
                <th style={styles.th}>Requerimiento</th>
                <th style={styles.th}>Artículos</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {listadoOrdenado.map((s) => (
                <tr
                  key={s.id}
                  style={{
                    borderBottom: "1px solid #eef1f3",
                    borderLeft: s.tiene_urgente ? "4px solid #a3402f" : "4px solid transparent",
                  }}
                >
                  <td className="celda-titulo" style={styles.td}>
                    <Link to={`/solicitud/${s.id}`} style={styles.linkFolio}>
                      #{String(s.id).padStart(3, "0")}
                    </Link>
                    {!!s.tiene_urgente && <div style={styles.badgeUrgente}>🔴 Urgente</div>}
                  </td>
                  {esStaff && <td data-label="Solicitante" style={styles.td}>{s.solicitante_nombre}</td>}
                  <td data-label="Fecha" style={styles.td}>{new Date(s.creado_en).toLocaleDateString("es-NI")}</td>
                  <td data-label="Área" style={styles.td}>{s.area_nombre}</td>
                  <td data-label="Requerimiento" style={styles.tdRequerimiento} title={s.descripcion_requerimiento}>
                    {s.descripcion_requerimiento}
                  </td>
                  {/* Solo la cantidad de productos (igual que el detalle: "3 artículos"). */}
                  <td data-label="Artículos" style={styles.td}>{s.total_items}</td>
                  <td data-label="Estado" style={styles.td}>
                    <Badge estado={estadoVisible(s.estado, s.total_cotizaciones, s.tiene_pendiente_aprobacion, s.en_atencion, s.tiene_pago_pendiente, s.tiene_pago_realizado)} mapa={mapaEstadosSolicitud()} />
                  </td>
                  <td className="celda-accion" style={styles.td}>
                    <Link to={`/solicitud/${s.id}`}>Ver</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        <div style={styles.paginacion}>
          <label style={{ fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
            Mostrar:
            <select
              value={tamanoPagina}
              onChange={(e) => {
                setTamanoPagina(e.target.value);
                setPagina(1);
              }}
              style={styles.filtroInput}
            >
              {TAMANOS_PAGINA.map((t) => (
                <option key={t} value={t}>
                  {t === "todas" ? "Todas" : t}
                </option>
              ))}
            </select>
          </label>

          {tamanoPagina !== "todas" && totalPaginas > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <button
                type="button"
                disabled={pagina === 1}
                onClick={() => setPagina((p) => p - 1)}
                style={{ ...styles.botonPaginacion, opacity: pagina === 1 ? 0.5 : 1 }}
              >
                ← Anterior
              </button>
              <span style={{ fontSize: "0.82rem" }}>
                Página {pagina} de {totalPaginas}
              </span>
              <button
                type="button"
                disabled={pagina === totalPaginas}
                onClick={() => setPagina((p) => p + 1)}
                style={{ ...styles.botonPaginacion, opacity: pagina === totalPaginas ? 0.5 : 1 }}
              >
                Siguiente →
              </button>
            </div>
          )}

          <span style={{ fontSize: "0.8rem", color: "#5b6472" }}>
            {totalListado} solicitud{totalListado === 1 ? "" : "es"} en total
          </span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  etapas: {
    display: "grid",
    gridTemplateColumns: "repeat(11, minmax(0, 1fr))",
    gap: "0.6rem",
    marginTop: "1rem",
  },
  etapa: {
    background: "var(--tema-suave, #eef3fa)",
    borderTop: "3px solid var(--tema-primario, #1f4e96)",
    borderRadius: 12,
    padding: "0.45rem 0.45rem 0.5rem",
    minWidth: 0,
  },
  etapaTitulo: {
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "var(--tema-titulo, #0b3c60)",
    padding: "0 0.2rem 0.4rem",
  },
  tiles: {
    display: "grid",
    gap: "0.4rem",
  },
  tile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "0.35rem",
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    padding: "0.75rem 0.3rem",
    boxShadow: "0 1px 3px rgba(27,42,74,0.05)",
    minWidth: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease",
  },
  tileActivo: {
    borderColor: "var(--tema-titulo, #0b3c60)",
    boxShadow: "0 0 0 2px rgba(27,42,74,0.15), 0 1px 3px rgba(27,42,74,0.05)",
  },
  tileTop: { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.35rem" },
  iconoTile: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
    flexShrink: 0,
  },
  tileNumero: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
    fontVariantNumeric: "tabular-nums",
  },
  tileTexto: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#5b6472",
    lineHeight: 1.15,
    minHeight: "2.3em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.25rem 1.4rem",
    marginTop: "1.75rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  cardHeaderSolicitudes: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: "0.5rem",
  },
  linkVerTodas: { fontSize: "0.88rem", fontWeight: 600, color: "var(--tema-primario, #2f6690)", textDecoration: "none" },
  botonNueva: {
    display: "inline-block",
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    textDecoration: "none",
  },
  iconoBusqueda: {
    position: "absolute",
    left: "0.55rem",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.8rem",
    pointerEvents: "none",
  },
  linkFolio: { color: "var(--tema-primario, #2f6690)", fontWeight: 600, textDecoration: "none" },
  badgeUrgente: {
    display: "inline-block",
    marginTop: "0.15rem",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 999,
    padding: "0.05rem 0.5rem",
    whiteSpace: "nowrap",
  },
  thOrdenable: {
    padding: "0.55rem 0.4rem",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.8rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  botonPaginacion: {
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.4rem 0.9rem",
    background: "#fff",
    cursor: "pointer",
    fontSize: "0.82rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  filtros: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.9rem",
    paddingTop: "0.9rem",
    borderTop: "1px solid #eef1f3",
  },
  etiquetaFecha: { display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.8rem", color: "#5b6472" },
  filtroInput: {
    padding: "0.4rem 0.5rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: "0.85rem",
  },
  botonLimpiar: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    padding: "0.4rem 0.8rem",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    background: "#fff",
    cursor: "pointer",
    fontSize: "0.85rem",
  },
  paginacion: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    marginTop: "1rem",
    paddingTop: "0.75rem",
    borderTop: "1px solid #eef1f3",
  },
  th: {
    padding: "0.55rem 0.4rem",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.8rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  td: { padding: "0.5rem" },
  tdRequerimiento: {
    padding: "0.5rem",
    maxWidth: 320,
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.4,
  },
};

export default Dashboard;
